import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/features/auth/authStore';
import { LOGIN_PATH } from '@/lib/auth';
import type { ApiResponse, AuthResponse } from '@/types/api';

/**
 * Consumes a magic link (/c or /c/:conversationId?token=...). Verifies the
 * short-lived token via /api/auth/magic-login, establishes a session, and jumps
 * straight to the target conversation. Credentials are never in the URL — only
 * a signed, expiring token.
 */
export function MagicLoginPage() {
  const [params] = useSearchParams();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against double-run in StrictMode
    ran.current = true;
    const token = params.get('token');
    if (!token) { setError('This link is missing its token.'); return; }

    apiClient
      .post<ApiResponse<AuthResponse & { conversationId?: string | null }>>('/api/auth/magic-login', { token })
      .then((res) => {
        const data = res.data.data;
        if (!data?.token || !data.user) throw new Error('bad response');
        setAuth(data.token, data.user);
        const target = conversationId || data.conversationId;
        navigate(target ? `/conversations?open=${target}` : '/conversations', { replace: true });
      })
      .catch(() => setError('This link is invalid or has expired. Please log in.'));
  }, [params, conversationId, navigate, setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors">
      <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
        {error ? (
          <>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button onClick={() => navigate(LOGIN_PATH)} className="text-sm text-slate-700 dark:text-slate-300 underline">
              Go to login
            </button>
          </>
        ) : (
          <>
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 dark:border-slate-700 border-t-slate-600 dark:border-t-slate-200" />
            <span className="text-sm">Signing you in…</span>
          </>
        )}
      </div>
    </div>
  );
}

export default MagicLoginPage;
